import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

/**
 * Premium dark page-hero block for inner pages.
 * Visual hierarchy: tight eyebrow → big display H1 → measured copy → CTAs → optional bullets.
 */
export default function PageHero({
    eyebrow,
    title,
    titleAccent,
    description,
    primaryCta,
    secondaryCta,
    bullets = [],
}) {
    return (
        <section
            data-testid="page-hero"
            className="relative pt-28 md:pt-36 pb-16 md:pb-24 overflow-hidden pqx-dark-panel"
        >
            <div className="absolute inset-0 pqx-grid-bg-dark opacity-50" />
            <div
                className="absolute right-[-10%] top-[20%] w-[420px] h-[420px] rounded-full pointer-events-none blur-3xl opacity-50"
                style={{ background: "radial-gradient(circle, rgba(255,64,64,0.20), transparent 60%)" }}
            />

            <div className="pqx-container relative z-10 max-w-5xl">
                {eyebrow && (
                    <span className="pqx-eyebrow !text-[#FF4040] pqx-reveal">{eyebrow}</span>
                )}
                <h1 className="pqx-h1 mt-5 text-white pqx-reveal pqx-delay-1">
                    {title}{" "}
                    {titleAccent && (
                        <span className="text-[#FF4040]">{titleAccent}</span>
                    )}
                </h1>
                {description && (
                    <p className="mt-6 md:mt-7 text-white/70 text-base md:text-lg lg:text-xl leading-relaxed max-w-3xl pqx-reveal pqx-delay-2">
                        {description}
                    </p>
                )}

                {(primaryCta || secondaryCta) && (
                    <div className="mt-8 md:mt-9 flex flex-wrap gap-3 pqx-reveal pqx-delay-3">
                        {primaryCta && (
                            <Link
                                to={primaryCta.to}
                                data-testid="page-hero-primary-cta"
                                className="pqx-btn-accent"
                            >
                                {primaryCta.label}
                                <ArrowUpRight className="w-4 h-4" />
                            </Link>
                        )}
                        {secondaryCta && (
                            <Link
                                to={secondaryCta.to}
                                data-testid="page-hero-secondary-cta"
                                className="pqx-btn-ghost-light"
                            >
                                {secondaryCta.label}
                            </Link>
                        )}
                    </div>
                )}

                {bullets.length > 0 && (
                    <div className="mt-10 md:mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 pqx-reveal pqx-delay-4">
                        {bullets.map((b, i) => (
                            <div
                                key={i}
                                className="pqx-glass-dark rounded-xl px-3.5 py-3 flex items-center gap-2.5"
                            >
                                <span className="w-1.5 h-1.5 rounded-full bg-[#FF4040] pqx-anim-blink shrink-0" />
                                <span className="text-[13px] md:text-sm text-white/82 leading-tight">{b}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}
